
import React, { useState } from 'react';
import { Card } from '../../Components/ui/card';
import { Input } from '../../Components/ui/input';
import { Label } from '../../Components/ui/label';
import { User, MapPin, Phone, Mail } from 'lucide-react';

export default function CustomerInfo({
  cardRef = null,
  customerData,
  onCustomerDataChange,
  highlightMissing = false,
  missingFields = [],
  onZipEdit = () => {},
  onZipComplete = () => {}
}) {
  const [streetSuggestions, setStreetSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Common Chicago area streets for autocomplete
  const chicagoStreets = [
    'Michigan Avenue', 'State Street', 'LaSalle Street', 'Wells Street', 'Clark Street',
    'Wabash Avenue', 'Dearborn Street', 'Franklin Street', 'Lake Street', 'Madison Street',
    'Monroe Street', 'Adams Street', 'Jackson Boulevard', 'Van Buren Street', 'Congress Parkway',
    'Harrison Street', 'Roosevelt Road', 'Cermak Road', 'Archer Avenue', 'Halsted Street',
    'Ashland Avenue', 'Western Avenue', 'California Avenue', 'Kedzie Avenue', 'Pulaski Road',
    'Cicero Avenue', 'Central Avenue', 'Austin Boulevard', 'Oak Park Avenue', 'Harlem Avenue',
    'North Avenue', 'Division Street', 'Chicago Avenue', 'Grand Avenue', 'Kinzie Street',
    'Huron Street', 'Erie Street', 'Ontario Street', 'Ohio Street', 'Hubbard Street'
  ];

  const handleStreetChange = (value) => {
    onCustomerDataChange({ ...customerData, street: value });
    
    if (value.length > 2) {
      const filtered = chicagoStreets.filter(street => 
        street.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5);
      setStreetSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectStreet = (street) => {
    onCustomerDataChange({ ...customerData, street });
    setShowSuggestions(false);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    return phoneRegex.test(phone);
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    const match = numbers.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      return !match[2] ? match[1] : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;
    }
    return value;
  };

  const handlePhoneChange = (value) => {
    const formatted = formatPhone(value);
    onCustomerDataChange({ ...customerData, phone: formatted });
  };

  const handleZipChange = (value) => {
    const sanitized = value.replace(/\D/g, '').slice(0, 5);
    onCustomerDataChange({ ...customerData, zip: sanitized });
    onZipEdit();
    if (sanitized.length === 5) {
      onZipComplete(sanitized);
    }
  };

  const isMissing = (key) => highlightMissing && missingFields.includes(key);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="feature-badge teal mx-auto">
          <User className="w-8 h-8" style={{ color: '#ffffff' }} />
        </div>
        <h3 className="section-heading mb-2" style={{color: '#1e5a5a'}}>
          Your Information
        </h3>
        <p style={{color: '#1e5a5a', marginBottom: '10px'}}>We need these details to complete your booking</p>
      </div>

      <Card ref={cardRef} className="info-card">
        <div className="info-card-body">
          {/* Name Fields */}
          <div className="info-card-grid">
            <div>
              <div className={isMissing('firstName') ? 'field-missing-block' : ''}>
                <Label
                  htmlFor="firstName"
                  className="font-medium info-label"
                  style={{ color: '#1e5a5a', fontWeight: 600 }}
                >
                  First Name *
                </Label>
                <Input
                  id="firstName"
                  value={customerData.firstName || ''}
                  onChange={(e) => onCustomerDataChange({ ...customerData, firstName: e.target.value })}
                  className={`mt-1 border focus:ring-2 focus:ring-opacity-50${isMissing('firstName') ? ' field-missing-input' : ''}`}
                  style={{
                    borderColor: 'rgba(30, 90, 90, 0.3)',
                    backgroundColor: '#f8f8f2'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#bfa14a';
                    e.target.style.boxShadow = '0 0 0 2px rgba(191, 161, 74, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(30, 90, 90, 0.3)';
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="John"
                />
              </div>
            </div>
            <div>
              <div className={isMissing('lastName') ? 'field-missing-block' : ''}>
                <Label
                  htmlFor="lastName"
                  className="font-medium info-label"
                  style={{ color: '#1e5a5a', fontWeight: 600 }}
                >
                  Last Name *
                </Label>
                <Input
                  id="lastName"
                  value={customerData.lastName || ''}
                  onChange={(e) => onCustomerDataChange({ ...customerData, lastName: e.target.value })}
                  className={`mt-1 border focus:ring-2 focus:ring-opacity-50${isMissing('lastName') ? ' field-missing-input' : ''}`}
                  style={{
                    borderColor: 'rgba(30, 90, 90, 0.3)',
                    backgroundColor: '#f8f8f2'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#bfa14a';
                    e.target.style.boxShadow = '0 0 0 2px rgba(191, 161, 74, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(30, 90, 90, 0.3)';
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="Doe"
                />
              </div>
            </div>
          </div>

          <div className="info-card-grid">
            <div className="info-card-section">
              <Label
                htmlFor="email"
                className="font-medium"
                style={{ color: '#1e5a5a', display: 'inline-flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}
              >
                <Mail className="w-4 h-4" />
                Email *
              </Label>
              <div className={isMissing('email') ? 'field-missing-block' : ''}>
                <Input
                  id="email"
                  type="email"
                  value={customerData.email || ''}
                  onChange={(e) => onCustomerDataChange({ ...customerData, email: e.target.value })}
                  className={`mt-1 border focus:ring-2 focus:ring-opacity-50${isMissing('email') ? ' field-missing-input' : ''}`}
                  style={{
                    borderColor: 'rgba(30, 90, 90, 0.3)',
                    backgroundColor: '#f8f8f2'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#bfa14a';
                    e.target.style.boxShadow = '0 0 0 2px rgba(191, 161, 74, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(30, 90, 90, 0.3)';
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="you@example.com"
                  autoComplete="email"
                  inputMode="email"
                />
              </div>
            </div>

            <div className="info-card-section">
              <Label
                htmlFor="phone"
                className="font-medium"
                style={{ color: '#1e5a5a', display: 'inline-flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}
              >
                <Phone className="w-4 h-4" />
                Phone Number *
              </Label>
              <div className={isMissing('phone') ? 'field-missing-block' : ''}>
                <Input
                  id="phone"
                  value={customerData.phone || ''}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  className={`mt-1 border focus:ring-2 focus:ring-opacity-50${isMissing('phone') ? ' field-missing-input' : ''}`}
                  style={{
                    borderColor: customerData.phone && !validatePhone(customerData.phone) ? '#dc2626' : 'rgba(30, 90, 90, 0.3)',
                    backgroundColor: '#f8f8f2'
                  }}
                  onFocus={(e) => {
                    if (!customerData.phone || validatePhone(customerData.phone)) {
                      e.target.style.borderColor = '#bfa14a';
                      e.target.style.boxShadow = '0 0 0 2px rgba(191, 161, 74, 0.2)';
                    }
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = customerData.phone && !validatePhone(customerData.phone) ? '#dc2626' : 'rgba(30, 90, 90, 0.3)';
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="(773) 555-0123"
                  maxLength={14}
                  inputMode="numeric"
                />
                {customerData.phone && !validatePhone(customerData.phone) && (
                  <p className="text-sm mt-1" style={{color: '#dc2626'}}>Please enter a valid phone number</p>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="info-card-section">
            <h4
              className="font-medium info-label"
              style={{ color: '#1e5a5a' }}
            >
              <MapPin className="w-4 h-4" />
              Address
            </h4>
            
            <div className="relative">
              <div className={isMissing('street') ? 'field-missing-block' : ''}>
                <Label htmlFor="street" className="font-medium info-label" style={{color: '#1e5a5a'}}>Street Address *</Label>
                <Input
                  id="street"
                  value={customerData.street || ''}
                  onChange={(e) => handleStreetChange(e.target.value)}
                  className={`mt-1 border focus:ring-2 focus:ring-opacity-50${isMissing('street') ? ' field-missing-input' : ''}`}
                  style={{
                    borderColor: 'rgba(30, 90, 90, 0.3)',
                    backgroundColor: '#f8f8f2'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#bfa14a';
                    e.target.style.boxShadow = '0 0 0 2px rgba(191, 161, 74, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(30, 90, 90, 0.3)';
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="123 Michigan Avenue"
                />
              </div>
              
              {showSuggestions && streetSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 border rounded-md shadow-lg" style={{backgroundColor: '#f8f8f2', borderColor: 'rgba(30, 90, 90, 0.3)'}}>
                  {streetSuggestions.map((street, index) => (
                    <button
                      key={index}
                      type="button"
                      className="w-full text-left px-3 py-2 border-b last:border-b-0 transition-colors"
                      style={{
                        color: '#1e5a5a',
                        borderBottomColor: 'rgba(30, 90, 90, 0.1)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'rgba(191, 161, 74, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                      onClick={() => selectStreet(street)}
                    >
                      {street}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="info-card-grid">
              <div>
                <div className={isMissing('city') ? 'field-missing-block' : ''}>
                  <Label htmlFor="city" className="font-medium info-label" style={{color: '#1e5a5a'}}>City *</Label>
                  <Input
                    id="city"
                    value={customerData.city || ''}
                    onChange={(e) => onCustomerDataChange({ ...customerData, city: e.target.value })}
                    className={`mt-1 border focus:ring-2 focus:ring-opacity-50${isMissing('city') ? ' field-missing-input' : ''}`}
                    style={{
                      borderColor: 'rgba(30, 90, 90, 0.3)',
                      backgroundColor: '#f8f8f2'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#bfa14a';
                      e.target.style.boxShadow = '0 0 0 2px rgba(191, 161, 74, 0.2)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 90, 90, 0.3)';
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder="Chicago"
                  />
                </div>
              </div>
              <div>
                <div className={isMissing('zip') ? 'field-missing-block' : ''}>
                  <Label htmlFor="zip" className="font-medium info-label" style={{color: '#1e5a5a'}}>ZIP Code *</Label>
                  <Input
                    id="zip"
                    value={customerData.zip || ''}
                    onChange={(e) => handleZipChange(e.target.value)}
                    className={`mt-1 border focus:ring-2 focus:ring-opacity-50${isMissing('zip') ? ' field-missing-input' : ''}`}
                    style={{
                      borderColor: 'rgba(30, 90, 90, 0.3)',
                      backgroundColor: '#f8f8f2'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#bfa14a';
                      e.target.style.boxShadow = '0 0 0 2px rgba(191, 161, 74, 0.2)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 90, 90, 0.3)';
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder="60601"
                    maxLength={5}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Phone */}
        </div>
      </Card>
    </div>
  );
}
